/**
 * features/terms/tabs/StatusTab.tsx
 * ------------------------------------------------------------------
 * Per-term status control. Sets the TermStatusDocument.status field
 * to open / closed / locked. Lifted from the old TermManagement
 * screen, narrowed to a single term, with a clearer status-card
 * layout (one row per state instead of the old three-card grid
 * showing all terms at once).
 * ------------------------------------------------------------------ */
import { useState } from 'react';
import { doc } from 'firebase/firestore';
// Batch C: undefined-safe write + save modal
import { setDoc } from '@/lib/firebase';
import { SaveConfirmation } from '@/components/ui/SaveConfirmation';
import { Lock, Unlock, ShieldAlert, Check } from 'lucide-react';
import { db } from '@/lib/firebase';
import { useData, COLLECTIONS } from '@/contexts/DataContext';
import { useAuth } from '@/contexts/AuthContext';
import { SCHOOL, TermKey } from '@/lib/school.config';
import { Card } from '@/components/ui/primitives';
import type { TermState } from '@/types';

interface Props {
  year: string;
  term: TermKey;
}

const STATES: {
  value: TermState;
  label: string;
  desc: string;
  icon: typeof Lock;
  cls: string;
}[] = [
  {
    value: 'open',
    label: 'Open',
    desc: 'All authorised staff can enter and edit data.',
    icon: Unlock,
    cls: 'text-emerald-600 bg-emerald-50 border-emerald-200',
  },
  {
    value: 'closed',
    label: 'Closed',
    desc: 'Data entry frozen. Only admin can make changes.',
    icon: Lock,
    cls: 'text-amber-600 bg-amber-50 border-amber-200',
  },
  {
    value: 'locked',
    label: 'Locked',
    desc: 'Fully sealed for archiving. Admin override only.',
    icon: ShieldAlert,
    cls: 'text-red-600 bg-red-50 border-red-200',
  },
];

export default function StatusTab({ year, term }: Props) {
  const { termStatuses } = useData();
  const { userData } = useAuth();
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [saveModal, setSaveModal] = useState<{
    show: boolean;
    status: 'saving' | 'success' | 'error';
    message: string;
  }>({ show: false, status: 'saving', message: '' });

  const current: TermState =
    termStatuses.find((t) => t.year === year && t.term === term)?.status ?? 'open';

  async function setState(status: TermState) {
    setBusy(status);
    setMsg(null);
    setSaveModal({ show: true, status: 'saving', message: `Changing term status to "${status}"…` });
    try {
      const id = `${year}_${term}`;
      await setDoc(
        doc(db, COLLECTIONS.termStatus, id),
        {
          id,
          year,
          term,
          status,
          updatedAt: new Date().toISOString(),
          updatedBy: userData?.uid,
        },
        { merge: true },
      );
      setSaveModal({ show: true, status: 'success', message: `${SCHOOL.terms[term].label} ${year} set to "${status}".` });
    } catch (e) {
      setSaveModal({ show: true, status: 'error', message: (e as Error).message });
    } finally {
      setBusy(null);
    }
  }

  return (
    <Card className="p-5">
      <h3 className="font-semibold text-sm text-gray-900 dark:text-white mb-1">
        Term status
      </h3>
      <p className="text-xs text-gray-500 mb-4">
        Controls whether teachers can enter or edit data for this term. Affects marks, comments, attendance, and everything else.
      </p>

      {msg && (
        <div className="mb-4 text-sm rounded-xl px-3 py-2.5 border bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:border-blue-700 flex items-center gap-2">
          <Check size={15} /> {msg}
        </div>
      )}

      <div className="space-y-2">
        {STATES.map((st) => {
          const Icon = st.icon;
          const active = current === st.value;
          return (
            <button
              key={st.value}
              disabled={busy !== null}
              onClick={() => setState(st.value)}
              className={`w-full text-left rounded-xl border p-4 transition-all ${
                active
                  ? st.cls + ' ring-2 ring-offset-1 dark:ring-offset-gray-900'
                  : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/50'
              }`}
            >
              <div className="flex items-center gap-2 font-semibold text-sm">
                <Icon size={15} />
                {st.label}
                {active && <Check size={14} className="ml-auto" />}
              </div>
              <p className="text-xs text-gray-500 mt-1 dark:text-gray-400">
                {st.desc}
              </p>
            </button>
          );
        })}
      </div>
      <SaveConfirmation
        show={saveModal.show}
        status={saveModal.status}
        message={saveModal.message}
        onDismiss={() => setSaveModal({ ...saveModal, show: false })}
      />
    </Card>
  );
}
